import { FilterId, type PrimaryFilterId } from '../../filters/types/filters';
import { isSelectableChartType } from '../ChartConfig/utils/is-selectable-chart-type';
import { getAvailableMetrics } from '../ChartConfig/utils/metric-mapping';
import { getAvailableSegment } from '../ChartConfig/utils/segment-mapping';
import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
} from '../PageConfigProvider/constants';
import type { TabContentAction } from '../PageConfigProvider/page-reducer';
import type {
  ChartType,
  SelectableChartType,
} from '../PageConfigProvider/types/chart';
import {
  type SecondaryDimensionOption,
  type SecondaryDimensionValues,
  secondaryDimensionOptions,
} from '../PageConfigProvider/types/dimension';
import type {
  DimensionSource,
  PlotFilterType,
  PlotOptions,
  PositionedItem,
} from '../PageConfigProvider/types/item';
import {
  type MetricValues,
  metricOptions,
} from '../PageConfigProvider/types/metric';

export interface LLMTabContentForDispatch {
  items: LLMPositionedItemForDispatch[];
  grid: { rows: number; columns: number };
  secondaryFilters: { id: string }[];
}

export interface LLMPositionedItemForDispatch {
  title: string | null;
  position: { x: number; y: number; width: number; height: number };
  plot?: {
    chartType: string;
    defaultMetricOptions?: string[];
    secondaryDimension?: string;
    textHtml?: string;
    textPrompt?: string;
    contentSize?: 'small' | 'medium' | 'large' | 'fullPage';
    plotFilters?: Array<{ type: string; value: number }>;
    dimensionSource?: {
      type: 'PRIMARY_DIMENSION' | 'TOP_SEGMENTS';
      segment?: SecondaryDimensionValues;
    };
    color?: string;
    showDataLabels?: boolean;
    showZebraStripe?: boolean;
  };
}

/**
 * Describes a single fallback applied to an LLM-suggested chart that didn't
 * match the dashboard capabilities matrix. Surfaced to users via a toast so
 * the rename + substitution isn't silent.
 */
export interface ChartCorrection {
  itemId: string;
  originalTitle: string | null;
  newTitle: string;
  original: {
    chartType: string;
    metrics: string[];
    secondaryDimension?: string;
  };
  corrected: {
    chartType: ChartType;
    metrics: MetricValues[];
    secondaryDimension?: SecondaryDimensionValues;
  };
  originalDescription: string;
  correctedDescription: string;
}

const SELECTABLE_CHART_TYPE_LABELS: Record<SelectableChartType, string> = {
  bar: 'Bar',
  'stacked-bar': 'Stacked Bar',
  'grouped-bar': 'Grouped Bar',
  'diverging-bar': 'Diverging Bar',
  'diverging-bar-combo': 'Diverging Bar Combo',
  line: 'Line',
  treemap: 'Treemap',
  volume: 'Volume',
  box: 'Boxplot',
  sankey: 'Sankey',
  chord: 'Chord',
  distribution: 'Distribution',
  map: 'Map',
  table: 'Table',
  text: 'Text',
  gauge: 'Gauge',
  'ai-summary': 'AI Summary',
};

function getChartTypeLabel(value: string): string {
  return (
    SELECTABLE_CHART_TYPE_LABELS[value as SelectableChartType] ??
    // Fall back to a humanized version for non-selectable types (e.g. share-of-segment).
    value
      .split('-')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ')
  );
}

function getMetricLabel(value: string): string {
  return metricOptions.find((option) => option.value === value)?.label ?? value;
}

function getSecondaryDimensionLabel(value: string): string {
  return (
    secondaryDimensionOptions.find((option) => option.value === value)?.label ??
    value
  );
}

function buildChartName({
  chartType,
  metrics,
  secondaryDimension,
}: {
  chartType: ChartType;
  metrics: MetricValues[];
  secondaryDimension?: SecondaryDimensionValues;
}): string {
  const chartTypeLabel = getChartTypeLabel(chartType);
  if (metrics.length === 0) {
    return chartTypeLabel;
  }
  const metricLabels = metrics.map(getMetricLabel).join(', ');
  // Avoid redundant suffixes like "Postings Volume Volume" when the metric
  // label already ends with the chart type label (e.g. volume charts).
  const baseName = metricLabels
    .toLowerCase()
    .endsWith(` ${chartTypeLabel.toLowerCase()}`)
    ? metricLabels
    : `${metricLabels} ${chartTypeLabel}`;
  if (secondaryDimension) {
    return `${baseName} by ${getSecondaryDimensionLabel(secondaryDimension)}`;
  }
  return baseName;
}

function describeChartSpec({
  chartType,
  metrics,
  secondaryDimension,
}: {
  chartType: string;
  metrics: string[];
  secondaryDimension?: string;
}): string {
  const parts: string[] = [getChartTypeLabel(chartType)];
  if (metrics.length > 0) {
    parts.push(metrics.map(getMetricLabel).join(', '));
  }
  if (secondaryDimension) {
    parts.push(`by ${getSecondaryDimensionLabel(secondaryDimension)}`);
  }
  return parts.join(' • ');
}

interface ValidatedPlotResult {
  plot: PlotOptions | undefined;
  /**
   * If any user-facing aspect of the chart spec (chartType, metric set, or
   * secondary dimension) had to be swapped, `correction` carries the before/
   * after so the caller can surface a toast + rename the chart.
   */
  correction?: Omit<ChartCorrection, 'itemId' | 'newTitle' | 'originalTitle'>;
}

function validateAndCorrectPlotOptions(
  plot: LLMPositionedItemForDispatch['plot'],
  primaryFilterId: PrimaryFilterId
): ValidatedPlotResult {
  if (!plot) return { plot: undefined };

  if (plot.chartType === 'text') {
    const textHtml =
      typeof plot.textHtml === 'string' ? plot.textHtml.trim() : '';
    if (!textHtml) {
      return { plot: undefined };
    }
    return {
      plot: {
        chartType: 'text',
        textHtml,
        ...(plot.textPrompt ? { textPrompt: plot.textPrompt } : {}),
        ...(plot.contentSize ? { contentSize: plot.contentSize } : {}),
      },
    };
  }

  const originalChartType = plot.chartType;
  const originalMetrics = plot.defaultMetricOptions ?? [];
  const originalSecondaryDimension = plot.secondaryDimension;

  const chartType: ChartType = isSelectableChartType(
    plot.chartType as ChartType
  )
    ? (plot.chartType as ChartType)
    : 'bar';

  const requestedMetrics = plot.defaultMetricOptions as MetricValues[];
  const requestedSecondaryDimension = plot.secondaryDimension as
    | SecondaryDimensionValues
    | undefined;

  const availableMetricGroups = getAvailableMetrics({
    primaryFilterId,
    chartType: chartType as SelectableChartType,
  });
  const availableMetrics = availableMetricGroups.flatMap((g) =>
    g.options.map((o) => o.value)
  );

  const validMetrics: MetricValues[] = (() => {
    const filtered = requestedMetrics.filter((m) =>
      availableMetrics.includes(m)
    );
    if (filtered.length > 0) {
      return filtered;
    }
    if (availableMetrics.length > 0) {
      const firstMetric = availableMetrics[0];
      return firstMetric ? [firstMetric] : [];
    }
    return [];
  })();

  if (validMetrics.length === 0) {
    return { plot: undefined };
  }

  const chartTypesWithSecondaryDimension: ChartType[] = [
    'grouped-bar',
    'stacked-bar',
    'box',
    'treemap',
  ];
  const shouldHaveSecondaryDimension =
    chartTypesWithSecondaryDimension.includes(chartType);

  const secondaryDimension: SecondaryDimensionValues | undefined = (() => {
    if (!shouldHaveSecondaryDimension) {
      return undefined;
    }
    const primaryMetric = validMetrics[0];
    if (!primaryMetric) {
      return undefined;
    }
    const availableSegments = getAvailableSegment({
      chartType,
      primaryFilterId,
      metric: primaryMetric,
    });
    const availableSegmentValues = availableSegments.map(
      (s: SecondaryDimensionOption) => s.value
    );
    if (
      requestedSecondaryDimension &&
      availableSegmentValues.includes(requestedSecondaryDimension)
    ) {
      return requestedSecondaryDimension;
    }
    const firstSegment = availableSegments[0];
    return firstSegment ? firstSegment.value : undefined;
  })();

  const dimensionSource: DimensionSource | undefined = (() => {
    if (!plot.dimensionSource) {
      return undefined;
    }
    if (plot.dimensionSource.type === 'PRIMARY_DIMENSION') {
      return 'primary';
    }
    if (
      plot.dimensionSource.type === 'TOP_SEGMENTS' &&
      plot.dimensionSource.segment
    ) {
      return plot.dimensionSource.segment as SecondaryDimensionValues;
    }
    return undefined;
  })();

  const finalConfig: PlotOptions = {
    chartType,
    defaultMetricOptions: validMetrics,
    secondaryDimension,
    // Mark only SINGLE-VALUE AI bars adaptive (no breakdown): a bar with a
    // `dimensionSource`/secondaryDimension renders one bar per segment (e.g.
    // headcount by top geographies) and must stay a bar. A plain snapshot bar
    // is one bar per entity, so when a single entity is selected it renders as
    // a number (bar→metric), matching the chat preview.
    ...(chartType === 'bar' &&
      !dimensionSource &&
      !secondaryDimension && { adaptive: true }),
    ...(dimensionSource && { dimensionSource }),
    ...(plot.plotFilters &&
      plot.plotFilters.length > 0 && {
        plotFilters: plot.plotFilters.map((f) => ({
          type: f.type as PlotFilterType,
          value: f.value,
        })),
      }),
    ...(plot.color && { color: plot.color }),
    ...(plot.showDataLabels !== undefined && {
      showDataLabels: plot.showDataLabels,
    }),
    ...(plot.showZebraStripe !== undefined && {
      showZebraStripe: plot.showZebraStripe,
    }),
  };

  const chartTypeChanged = originalChartType !== chartType;
  const metricsChanged = (() => {
    if (originalMetrics.length !== validMetrics.length) {
      return true;
    }
    return originalMetrics.some((m, i) => m !== validMetrics[i]);
  })();
  const secondaryDimensionChanged =
    (originalSecondaryDimension ?? undefined) !== secondaryDimension;

  if (!chartTypeChanged && !metricsChanged && !secondaryDimensionChanged) {
    return { plot: finalConfig };
  }

  const correction: ValidatedPlotResult['correction'] = {
    original: {
      chartType: originalChartType,
      metrics: originalMetrics,
      ...(originalSecondaryDimension && {
        secondaryDimension: originalSecondaryDimension,
      }),
    },
    corrected: {
      chartType,
      metrics: validMetrics,
      ...(secondaryDimension && { secondaryDimension }),
    },
    originalDescription: describeChartSpec({
      chartType: originalChartType,
      metrics: originalMetrics,
      secondaryDimension: originalSecondaryDimension,
    }),
    correctedDescription: describeChartSpec({
      chartType,
      metrics: validMetrics,
      secondaryDimension,
    }),
  };

  return { plot: finalConfig, correction };
}

interface ConvertedItem {
  item: PositionedItem;
  correction?: ChartCorrection;
}

function convertLLMItemToPositionedItem(
  item: LLMPositionedItemForDispatch,
  primaryFilterId: PrimaryFilterId
): ConvertedItem {
  const { plot, correction } = validateAndCorrectPlotOptions(
    item.plot,
    primaryFilterId
  );

  const id = crypto.randomUUID();

  if (!correction || !plot) {
    return {
      item: {
        id,
        title: item.title,
        position: item.position,
        plot,
      },
    };
  }

  const newTitle = buildChartName({
    chartType: correction.corrected.chartType,
    metrics: correction.corrected.metrics,
    secondaryDimension: correction.corrected.secondaryDimension,
  });

  return {
    item: {
      id,
      title: newTitle,
      position: item.position,
      plot,
    },
    correction: {
      itemId: id,
      originalTitle: item.title,
      newTitle,
      ...correction,
    },
  };
}

export interface ValidatedLlmContent {
  items: PositionedItem[];
  corrections: ChartCorrection[];
}

export function getValidatedPositionedItemsFromLlmContent(
  content: LLMTabContentForDispatch,
  primaryFilterId: PrimaryFilterId
): ValidatedLlmContent {
  const corrections: ChartCorrection[] = [];
  const items = content.items
    .map((rawItem) => convertLLMItemToPositionedItem(rawItem, primaryFilterId))
    .filter(({ item }) => {
      if (!item.plot) {
        return false;
      }
      if (item.plot.chartType === 'text') {
        return Boolean(item.plot.textHtml?.trim());
      }
      if (
        !item.plot.defaultMetricOptions ||
        item.plot.defaultMetricOptions.length === 0
      ) {
        return false;
      }
      return true;
    })
    .map(({ item, correction }) => {
      if (correction) {
        corrections.push(correction);
      }
      return item;
    });

  return { items, corrections };
}

/** One chart on a new custom page: tall enough to read without adding grid rows first. */
const GLOBAL_ASSISTANT_SINGLE_CHART_CUSTOM_PAGE_CHART_HEIGHT_ROWS = 22;

/**
 * Rebuilds LLM-shaped tab content from a validated `PositionedItem` so global-assistant
 * flows can re-dispatch through `applyLlmTabContentToTabDispatch` (e.g. open single chart on a custom page).
 */
export function buildLlmTabContentFromPositionedItemForGlobalAssistant(
  item: PositionedItem
): LLMTabContentForDispatch {
  const plot = item.plot;
  const llmPlot: LLMPositionedItemForDispatch['plot'] = (() => {
    if (!plot) {
      return undefined;
    }

    if (plot.chartType === 'text') {
      return {
        chartType: 'text',
        ...(plot.textHtml !== undefined ? { textHtml: plot.textHtml } : {}),
        ...(plot.textPrompt !== undefined
          ? { textPrompt: plot.textPrompt }
          : {}),
        ...(plot.contentSize !== undefined
          ? { contentSize: plot.contentSize }
          : {}),
      };
    }

    const dimensionSource = (() => {
      const ds = plot.dimensionSource;
      if (ds === undefined) {
        return undefined;
      }
      if (ds === 'primary') {
        return { type: 'PRIMARY_DIMENSION' as const };
      }
      return {
        type: 'TOP_SEGMENTS' as const,
        segment: ds as SecondaryDimensionValues,
      };
    })();

    return {
      chartType: plot.chartType ?? 'bar',
      defaultMetricOptions: plot.defaultMetricOptions ?? [],
      ...(plot.secondaryDimension !== undefined && {
        secondaryDimension: plot.secondaryDimension,
      }),
      ...(plot.plotFilters &&
        plot.plotFilters.length > 0 && {
          plotFilters: plot.plotFilters.map((f) => ({
            type: String(f.type),
            value: f.value,
          })),
        }),
      ...(dimensionSource !== undefined && { dimensionSource }),
      ...(plot.color !== undefined && { color: plot.color }),
      ...(plot.showDataLabels !== undefined && {
        showDataLabels: plot.showDataLabels,
      }),
      ...(plot.showZebraStripe !== undefined && {
        showZebraStripe: plot.showZebraStripe,
      }),
    };
  })();

  return {
    items: [
      {
        title: item.title,
        position: {
          x: 0,
          y: 0,
          width: DEFAULT_GRID_COLUMNS,
          height: Math.min(
            GLOBAL_ASSISTANT_SINGLE_CHART_CUSTOM_PAGE_CHART_HEIGHT_ROWS,
            DEFAULT_GRID_ROWS
          ),
        },
        plot: llmPlot,
      },
    ],
    grid: {
      rows: DEFAULT_GRID_ROWS,
      columns: DEFAULT_GRID_COLUMNS,
    },
    secondaryFilters: [],
  };
}

export interface AppliedLlmTabContent {
  /**
   * The validated `PositionedItem`s that were dispatched to the tab — these
   * carry the exact ids the widgets mount with. Callers arming the narration
   * collector MUST reuse these ids (rather than re-converting the LLM content,
   * which mints fresh `crypto.randomUUID()`s that never match the mounted
   * widgets and so cause the collector to time out with an empty digest).
   */
  items: PositionedItem[];
  corrections: ChartCorrection[];
}

/**
 * PURE variant of `applyLlmTabContentToTabDispatch` for the resolve-first
 * page-gen flow (Phase C): seed a not-yet-mounted draft tab's content with a
 * pre-generated LLM layout — same validation, same grid expansion — without a
 * dispatch target. Used by `NewCustomPage` when a generated layout is stashed
 * for the draft path, so the page MOUNTS complete instead of building live.
 */
export function seedTabContentWithLlmContent<
  T extends { items?: unknown; grid?: unknown },
>(
  tabContent: T,
  content: LLMTabContentForDispatch,
  primaryFilterId: PrimaryFilterId
): T {
  const { items: validatedItems } = getValidatedPositionedItemsFromLlmContent(
    content,
    primaryFilterId
  );
  const maxItemExtent = validatedItems.reduce(
    (max, item) => Math.max(max, item.position.y + item.position.height),
    0
  );
  const requestedRows = content.grid.rows ?? DEFAULT_GRID_ROWS;
  const rows = Math.max(requestedRows, maxItemExtent, DEFAULT_GRID_ROWS);
  return {
    ...tabContent,
    items: validatedItems,
    grid: { rows, columns: content.grid.columns ?? DEFAULT_GRID_COLUMNS },
  };
}

export function applyLlmTabContentToTabDispatch(
  dispatch: (action: TabContentAction) => void,
  content: LLMTabContentForDispatch,
  primaryFilterId: PrimaryFilterId,
  options?: {
    /**
     * Called once after the dispatch if any LLM-suggested charts had to be
     * swapped for valid combinations from the capabilities matrix.
     */
    onCorrections?: (corrections: ChartCorrection[]) => void;
  }
): AppliedLlmTabContent {
  const { items: validatedItems, corrections } =
    getValidatedPositionedItemsFromLlmContent(content, primaryFilterId);
  dispatch({
    type: 'SET_ITEMS',
    payload: validatedItems,
  });
  // Defensive: if the LLM-provided grid is shorter than the bottom edge of any
  // validated item, expand the grid so plots near the bottom aren't cut off.
  const maxItemExtent = validatedItems.reduce(
    (max, item) => Math.max(max, item.position.y + item.position.height),
    0
  );
  const requestedRows = content.grid.rows ?? DEFAULT_GRID_ROWS;
  const rows = Math.max(requestedRows, maxItemExtent, DEFAULT_GRID_ROWS);
  dispatch({
    type: 'SET_GRID',
    payload: {
      rows,
      columns: content.grid.columns ?? DEFAULT_GRID_COLUMNS,
    },
  });

  if (corrections.length > 0) {
    options?.onCorrections?.(corrections);
  }

  return { items: validatedItems, corrections };
}

export const PRIMARY_FILTER_BY_AI_ENTITY: Record<
  'company' | 'geography' | 'role',
  PrimaryFilterId
> = {
  company: FilterId.COMPANY,
  geography: FilterId.GEOGRAPHY,
  role: FilterId.ROLE,
};
